import { fabric } from "fabric";
import {
  ANNOTATION_COLOR,
  ANNOTATION_LINE_WIDTH_VALUE,
  ANNOTATION_TYPE,
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
} from "@/constants/annotationStatus";
import { AnnotationUtils } from "@/js/annotation/AnnotationUtils";
import { getArrowDefaultConfig, getImageDefaultConfig, getTextDefaultConfig } from "@/js/annotation/formatToFabric";
import { cloneDeep, floor } from "lodash-es";
import store from "@/store";

export class AnnotationFabric extends AnnotationUtils {
  constructor(config = { canvasId, sourceCanvas, isHideCanvas }) {
    super();
    this.config(config);
  }

  [ANNOTATION_TYPE.SELECT]() {
    this.Fabric.isDrawingMode = false;
  }

  [ANNOTATION_TYPE.TEXT](data) {
    const config = getTextDefaultConfig(data);
    if (config.isRedraw) {
      this.Fabric.add(new fabric.IText(config.text, config));
    } else {
      this.TextUtils.bindTextMouseEvent(this.Fabric, config);
    }
  }

  [ANNOTATION_TYPE.PEN]({ lineWidth = ANNOTATION_LINE_WIDTH_VALUE.NORMAL, color = ANNOTATION_COLOR.RED }) {
    this.Fabric.freeDrawingBrush.color = color;
    this.Fabric.freeDrawingBrush.width = lineWidth;
  }

  [ANNOTATION_TYPE.ARROW](data) {
    const config = getArrowDefaultConfig(data);
    if (config.isRedraw) {
      this.Fabric.add(new fabric.Arrow([config.startX, config.startY, config.endX, config.endY], config));
    } else {
      this.ArrowUtils.prepareAndBindEvent(this.Fabric, this.canvasHeight, this.canvasWidth, config);
    }
  }

  [ANNOTATION_TYPE.SHAPE](data) {
    const newData = { ...data, type: ANNOTATION_TYPE.SHAPE };
    if (newData.isRedraw) {
      const item = this.ShapeUtils.generateItem(newData);
      this.Fabric.add(item);
    } else {
      this.ShapeUtils.prepareAndBindEvent(this.Fabric, this.canvasHeight, this.canvasWidth, newData);
    }
  }

  [ANNOTATION_TYPE.BLUR](data) {
    const newData = { ...data, type: ANNOTATION_TYPE.BLUR };
    if (newData.isRedraw) {
      const item = this.ShapeUtils.generateItem(newData);
      this.Fabric.add(item);
    } else {
      this.ShapeUtils.prepareAndBindEvent(this.Fabric, this.canvasHeight, this.canvasWidth, newData);
    }
  }

  async [ANNOTATION_TYPE.IMAGE](data) {
    const config = getImageDefaultConfig(data);
    return new Promise((resolve) => {
      fabric.Image.fromObject(config, (img) => {
        try {
          const isFirstTimeToLoad = !config.isRedraw;
          if (isFirstTimeToLoad) {
            // prevent image from being too large
            const GAP = 50;
            const scaleRatio = Math.min(
              1,
              (this.canvasWidth - GAP) / img.width,
              (this.canvasHeight - GAP) / img.height
            );
            img.scale(Number(scaleRatio.toFixed(2)));
          }
          this.Fabric.add(img);
          resolve();
        } catch (error) {
          console.error("load annotation image error", error);
          resolve();
        }
      });
    });
  }

  reDrawPen(data) {
    if (data.path) {
      const path = new fabric.Path(data.path, {
        ...data,
        isRedraw: true,
      });
      this.Fabric.add(path);
    }
  }

  toggleDrawMode(isDrawingMode = false) {
    this.Fabric.isDrawingMode = isDrawingMode;
  }

  toggleAnnotationEvent(isActive = true) {
    this.Fabric.getObjects().forEach((item) => (item.evented = isActive));
    this.Fabric.requestRenderAll();
  }

  unselect() {
    this.Fabric.discardActiveObject();
    this.Fabric.requestRenderAll();
  }

  toggleBlurItem(selectedId) {
    const items = this.Fabric.getObjects();
    items.forEach((item) => {
      if (item.type === ANNOTATION_TYPE.BLUR) {
        item.opacity = selectedId === item.id ? 1 : 0;
      }
    });
  }

  deleteDataArray(deletedId) {
    const object = this.Fabric.getItemById(deletedId);
    if (object) {
      this.Fabric.remove(object);
    } else {
      console.error(`object (${object}) or deleteId (${deletedId}) is invalid`);
    }
  }

  selectDataArray(selectedId) {
    const target = this.Fabric.getItemById(selectedId);
    if (!target) return null;
    const canScaleUnlimitRatio = [ANNOTATION_TYPE.BLUR, ANNOTATION_TYPE.SHAPE].includes(target.type);
    target.setControlVisible("ml", canScaleUnlimitRatio);
    target.setControlVisible("mb", canScaleUnlimitRatio);
    target.setControlVisible("mr", canScaleUnlimitRatio);
    target.setControlVisible("mt", canScaleUnlimitRatio);
    this.Fabric.setActiveObject(target);
    this.Fabric.requestRenderAll();
  }

  updateSelectedStyle({ id, key, value }) {
    const target = this.Fabric.getItemById(id);
    if (!target) return null;
    target.set({ [key]: value });
    this.Fabric.requestRenderAll();
  }

  changeIsHideCanvas(isHideCanvas) {
    this.isHideCanvas = isHideCanvas;
    const items = this.Fabric.getObjects();
    items.forEach((item) => {
      item.opacity = +!isHideCanvas;
    });
    this.Fabric.requestRenderAll();
  }

  detectIsAnnotationShowUp(currentTime, stepId) {
    if (this.isHideCanvas || !stepId) return;
    const items = this.Fabric.getObjects();
    const { elements: annotationFromVuex } = store.getters["annotation/getters_get_annotation_by_id"](stepId);

    if (!items || !annotationFromVuex) {
      console.error(`items or annotationFromVuex is invalid`);
      return;
    }

    items.forEach((item) => {
      const { endTime, startTime } = annotationFromVuex.find(({ id }) => id === item.id);
      const isShowUp = endTime >= currentTime && Math.floor(startTime) <= currentTime;
      item.opacity = +isShowUp;
      item.selectable = isShowUp;
    });
    this.Fabric.requestRenderAll();
  }

  handleResize({ width, height }) {
    let _height = height;
    const ORIGINAL_CANVAS_RECTANGLE_PERCENTAGE = 1.77;
    if (floor(width / height, 2) !== ORIGINAL_CANVAS_RECTANGLE_PERCENTAGE) {
      _height = width / ORIGINAL_CANVAS_RECTANGLE_PERCENTAGE;
    }
    this.Fabric.setWidth(width);
    this.Fabric.setHeight(_height);
    this.canvasHeight = _height;
    this.canvasWidth = width;
  }

  async loadData(elements = []) {
    this.Fabric.clear();
    const isDoNotNeedToCalcuatePercentage = this.canvasWidth === CANVAS_WIDTH && this.canvasHeight === CANVAS_HEIGHT;
    const tempSelectedId = this.watch?.selectedId;
    for (const element of elements) {
      const _element = isDoNotNeedToCalcuatePercentage ? element : this._scaleElementToPlayerRatio(element);
      if (element.type !== ANNOTATION_TYPE.PEN) {
        await this[element.type]({
          ..._element,
          isRedraw: true,
        });
      } else {
        this.reDrawPen(_element);
      }
    }
    if (tempSelectedId) {
      this.watch.selectedId = tempSelectedId;
      this.selectDataArray(tempSelectedId);
    }
  }

  _scaleElementToPlayerRatio(element) {
    const { top, left, scaleX = 1, scaleY = 1 } = element;
    const windowHeightPercentage = this.canvasHeight / CANVAS_HEIGHT;
    const windowWidthPercentage = this.canvasWidth / CANVAS_WIDTH;
    return cloneDeep({
      ...element,
      top: top * windowHeightPercentage,
      left: left * windowWidthPercentage,
      scaleY: scaleY * windowHeightPercentage,
      scaleX: scaleX * windowWidthPercentage,
    });
  }

  _createArrow() {
    fabric.Arrow = fabric.util.createClass(fabric.Line, {
      type: ANNOTATION_TYPE.ARROW,

      initialize: function (element, options) {
        options || (options = {});
        this.callSuper("initialize", element, options);
      },

      toObject: function () {
        return fabric.util.object.extend(this.callSuper("toObject"));
      },

      _render: function (ctx) {
        const ARROW_WIDTH = 20;
        function calculateDistance(x1, y1, x2, y2) {
          const dx = x2 - x1;
          const dy = y2 - y1;
          return Math.sqrt(dx * dx + dy * dy);
        }

        function calculateNewPoint(originalLinePoints) {
          const { x1, y1, x2, y2 } = originalLinePoints;
          const originalLength = calculateDistance(x1, y1, x2, y2);
          const newLength = originalLength - (ARROW_WIDTH - 2); // 2 is overlap gap, to prevent to show empty gap
          const scale = newLength / originalLength;
          const newX = x1 + scale * (x2 - x1);
          const newY = y1 + scale * (y2 - y1);
          return { x: newX, y: newY };
        }

        // draw line, refs fabric.js, line 18408 render function
        // http://fabricjs.com/docs/fabric.js.html#line18408
        const originalLinePoints = this.calcLinePoints();
        const newPoint = calculateNewPoint(originalLinePoints);
        ctx.beginPath();
        ctx.moveTo(originalLinePoints.x1, originalLinePoints.y1);
        ctx.lineTo(newPoint.x, newPoint.y); // custom here
        ctx.lineWidth = this.strokeWidth;
        ctx.strokeStyle = this.stroke;
        ctx.fillStyle = this.stroke;
        this.stroke && this._renderStroke(ctx);
        ctx.save();

        // draw arrow
        const xDiff = this.x2 - this.x1;
        const yDiff = this.y2 - this.y1;
        const angle = Math.atan2(yDiff, xDiff);
        ctx.translate((this.x2 - this.x1) / 2, (this.y2 - this.y1) / 2);
        ctx.rotate(angle);
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(-1 * ARROW_WIDTH, ARROW_WIDTH / 2);
        ctx.lineTo(-1 * ARROW_WIDTH, (-1 * ARROW_WIDTH) / 2);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      },
      clipTo: function (ctx) {
        this._render(ctx);
      },
    });
    fabric.Arrow.fromObject = function (object, callback) {
      callback && callback(new fabric.Arrow([object.x1, object.y1, object.x2, object.y2], object));
    };
  }

  _changeSelectOutline() {
    fabric.Object.prototype.setControlsVisibility({
      bl: true, // bottom left
      br: true, // bottom right
      mb: false, // bottom middle
      ml: false, // middle left
      mr: false, // middle right
      mt: false, // top middle
      tl: true, // top left
      tr: true, // top right
      mtr: false, // middle top rotate
    });
    fabric.Object.prototype.borderColor = "#979797";
    fabric.Object.prototype.cornerSize = 5;
    fabric.Object.prototype.transparentCorners = false;
    fabric.Object.prototype.cornerColor = "#fff";
  }

  _addCustomizedFunction() {
    fabric.Canvas.prototype.getItemById = function (id) {
      return this.getObjects().find((item) => item.id === id);
    };
  }

  _checkBoundaries(target) {
    if (!target) {
      console.error(`This target is invalid`);
      return;
    }
    const gap = 5;
    const { width, height } = target.scaleX !== 1 || target.scaleY !== 1 ? target.getBoundingRect() : target;
    if (target.top < 0 || target.left < 0) {
      target.top = Math.max(target.top, gap);
      target.left = Math.max(target.left, gap);
    }

    // if user paste text that is too long, it might overlay the boundary of canvas.
    const LEFT_MAXIMUM = Math.max(target.canvas.width - width - gap, gap);
    const TOP_MAXIMUM = Math.max(target.canvas.height - height - gap, gap);
    if (target.top >= TOP_MAXIMUM || target.left >= LEFT_MAXIMUM) {
      target.top = Math.min(TOP_MAXIMUM, target.top);
      target.left = Math.min(LEFT_MAXIMUM, target.left);
    }
    target.setCoords();
  }

  _checkTextWrapping(target) {
    const generateWrappingText = (target) => {
      const sourceText = target.text.replace(/\n/g, "");
      const selectionOutlineCornerSize = 5; // L221
      const gap = 75;
      let line = "";
      const lineArray = [];
      for (let i = 0; i < sourceText.length; i++) {
        const width = target._cacheContext.measureText(line).width;
        line = line + sourceText[i];
        if (width + selectionOutlineCornerSize + gap > this.canvasWidth) {
          lineArray.push(line);
          line = "";
        }
      }

      if (line !== "") {
        lineArray.push(line);
      }
      return lineArray.join("\n");
    };
    const isNeedToWrap = target.calcTextWidth() > this.canvasWidth;
    if (isNeedToWrap) {
      const text = generateWrappingText(target);
      target.set({ text });
      target.hiddenTextarea.value = text;
      this.Fabric.renderAll();
    }
  }

  _addInterceptionForCheckBoundaries() {
    this.Fabric.on("object:moving", (event) => {
      const { target } = event;
      this._checkBoundaries(target);
    });

    const MAXIMUM = { left: 0, top: 0, scaleX: 0, scaleY: 0, width: 0, height: 0 };
    const keys = Object.keys(MAXIMUM);
    this.Fabric.on("object:scaling", (event) => {
      const { target } = event;
      if (!target) {
        return;
      }
      target.setCoords();
      const newObject = target.getBoundingRect();
      if (
        newObject.width + newObject.left >= target.canvas.width ||
        newObject.height + newObject.top >= target.canvas.height ||
        newObject.top < 0 ||
        newObject.left < 0
      ) {
        keys.forEach((key) => {
          target[key] = MAXIMUM[key];
        });
      } else {
        keys.forEach((key) => {
          MAXIMUM[key] = target[key];
        });
      }
    });

    this.Fabric.on("object:added", (event) => {
      const { target } = event;
      this._checkBoundaries(target);
    });

    this.Fabric.on("text:changed", (event) => {
      const { target } = event;

      this._checkTextWrapping(target);
      this._checkTruncateText(target);
      this._checkBoundaries(target);
      this.Fabric.requestRenderAll();
    });
  }

  _checkTruncateText(target) {
    if (target.calcTextHeight() < this.canvasHeight) {
      return;
    }
    const newTextLines = target.textLines.slice(0, 7);
    newTextLines[newTextLines.length - 1] = newTextLines[newTextLines.length - 1].slice(0, -3) + "...";
    const text = newTextLines.join("\n");
    target.set({ text });
    target.hiddenTextarea.value = text;
  }
  config(config) {
    this._createArrow();
    this._changeSelectOutline();
    this._addCustomizedFunction();

    this.sourceCanvas = config.sourceCanvas;
    this.canvasWidth = config.sourceCanvas.clientWidth;
    this.canvasHeight = config.sourceCanvas.clientHeight;
    fabric.Object.prototype.lockScalingFlip = true;
    this.Fabric = new fabric.Canvas(config.canvasId, {
      width: this.canvasWidth,
      height: this.canvasHeight,
    });
    this._addInterceptionForCheckBoundaries();
    this.center = {
      x: config.sourceCanvas.clientWidth / 2,
      y: config.sourceCanvas.clientHeight / 2,
    };
    this.videoEndTime = config.videoEndTime;
    this.watch = config.watch;
    this.isHideCanvas = config.isHideCanvas;
  }
}
